import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  TrashIcon,
  PlayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EyeIcon,
  EyeSlashIcon,
  AdjustmentsHorizontalIcon,
  TableCellsIcon,
  MagnifyingGlassIcon,
  DocumentMagnifyingGlassIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const AUTH_TYPES = [
  { id: 'none', name: 'No Auth' },
  { id: 'basic', name: 'Basic Auth' },
  { id: 'bearer', name: 'Bearer Token' },
  { id: 'api_key', name: 'API Key' }
];

const PAGINATION_TYPES = [
  { id: 'none', name: 'No Pagination', description: 'Single request, all data in one response' },
  { id: 'page', name: 'Page-based', description: 'Uses page number parameter (e.g., ?page=1)' },
  { id: 'offset', name: 'Offset-based', description: 'Uses offset/limit parameters' },
  { id: 'cursor', name: 'Cursor-based', description: 'Uses cursor token for next page' },
  { id: 'link-header', name: 'Link Header', description: 'RFC 5988 Link header (GitHub style)' }
];

// Incremental sync strategy options
const INCREMENTAL_STRATEGIES = [
  {
    id: 'query',
    name: 'Query Parameter',
    description: 'Add filter as URL query parameter',
    example: '?updated_since=2024-01-01T00:00:00Z'
  },
  {
    id: 'path',
    name: 'Path Parameter',
    description: 'Replace placeholder in URL path',
    example: '/api/events/2024-01-01/latest'
  },
  {
    id: 'header',
    name: 'Header-based',
    description: 'Send value in HTTP header',
    example: 'If-Modified-Since: Wed, 21 Oct 2024 07:28:00 GMT'
  },
  {
    id: 'body',
    name: 'Body Parameter',
    description: 'Include filter in request body (POST/PUT)',
    example: '{ "filter": { "updatedAfter": "2024-01-01" } }'
  },
  {
    id: 'cursor',
    name: 'Cursor + State',
    description: 'Track incremental value with cursor-based pagination',
    example: 'Requires cursor pagination • Tracks timestamp for resume capability',
    requiresCursor: true
  }
];

const DATE_FORMATS = [
  { id: 'iso8601', name: 'ISO 8601', example: '2024-01-01T00:00:00Z', description: 'Standard ISO format with time' },
  { id: 'iso8601_date', name: 'ISO 8601 Date', example: '2024-01-01', description: 'Date only (YYYY-MM-DD)' },
  { id: 'unix', name: 'Unix Timestamp', example: '1704067200', description: 'Seconds since epoch' },
  { id: 'unix_ms', name: 'Unix Milliseconds', example: '1704067200000', description: 'Milliseconds since epoch' },
  { id: 'rfc2822', name: 'RFC 2822', example: 'Wed, 21 Oct 2024 07:28:00 GMT', description: 'HTTP date format' },
  { id: 'custom', name: 'Custom Format', example: 'Use column type formatting', description: 'Based on column type setting' }
];

// Helper to generate live preview of formatted date
const formatDatePreview = (formatId) => {
  const now = new Date();
  switch (formatId) {
    case 'iso8601':
      return now.toISOString();
    case 'iso8601_date':
      return now.toISOString().split('T')[0];
    case 'unix':
      return Math.floor(now.getTime() / 1000).toString();
    case 'unix_ms':
      return now.getTime().toString();
    case 'rfc2822':
      return now.toUTCString();
    case 'custom':
      return 'Based on column type';
    default:
      return now.toISOString();
  }
};

const METHOD_COLORS = {
  GET: 'bg-emerald-500',
  POST: 'bg-amber-500',
  PUT: 'bg-blue-500',
  PATCH: 'bg-purple-500',
  DELETE: 'bg-red-500'
};

export default function RestApiSource({ companyId, onSuccess, onBack }) {
  const [name, setName] = useState('');
  const [method, setMethod] = useState('GET');
  const [url, setUrl] = useState('');
  const [activeTab, setActiveTab] = useState('params');
  
  // Params
  const [queryParams, setQueryParams] = useState([{ key: '', value: '', enabled: true }]);
  const [pathParams, setPathParams] = useState([{ key: '', value: '', enabled: true }]);
  
  // Headers
  const [headers, setHeaders] = useState([
    { key: 'Content-Type', value: 'application/json', enabled: true },
    { key: '', value: '', enabled: true }
  ]);
  
  // Auth
  const [authType, setAuthType] = useState('none');
  const [authConfig, setAuthConfig] = useState({
    username: '',
    password: '',
    token: '',
    apiKeyName: '',
    apiKeyValue: '',
    apiKeyIn: 'header'
  });
  const [showPassword, setShowPassword] = useState(false);
  
  // Body
  const [bodyType, setBodyType] = useState('json');
  const [bodyContent, setBodyContent] = useState('{\n  \n}');
  
  // Data extraction
  const [dataPath, setDataPath] = useState('');
  const [responseStructure, setResponseStructure] = useState(null);
  
  // Flattening config
  const [flattenConfig, setFlattenConfig] = useState({
    maxDepth: 3,
    separator: '_',
    arrayHandling: 'stringify'
  });
  
  // Pagination config
  const [paginationType, setPaginationType] = useState('none');
  const [paginationConfig, setPaginationConfig] = useState({
    pageParam: 'page',
    limitParam: 'limit',
    limit: 100,
    cursorParam: 'cursor',
    cursorPath: 'meta.next_cursor',
    hasMorePath: '',
    totalPath: '',
    maxPages: 1000
  });
  
  // Incremental sync config
  const [incrementalEnabled, setIncrementalEnabled] = useState(false);
  const [incrementalConfig, setIncrementalConfig] = useState({
    column: '',
    columnType: 'auto',
    strategy: 'query',
    strategyConfig: {
      // Query
      paramName: '',
      format: 'iso8601', // Date format for query param
      // Path
      template: '',
      placeholder: '',
      pathFormat: 'iso8601', // Date format for path param
      // Header
      headerName: 'If-Modified-Since',
      headerFormat: 'iso8601', // Date format for header
      // Body
      paramPath: ''
    }
  });
  
  // Column selection
  const [columns, setColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState(new Set());
  const [columnSearchTerm, setColumnSearchTerm] = useState('');
  
  // Deduplication key selection (for merge operations)
  const [deduplicationKeys, setDeduplicationKeys] = useState([]);
  
  // Response & State
  const [response, setResponse] = useState(null);
  const [flattenedPreview, setFlattenedPreview] = useState(null);
  const [rawPreview, setRawPreview] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [isFlattenLoading, setIsFlattenLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [step, setStep] = useState(1); // 1: config, 2: data extraction, 3: preview & columns
  
  // Auto-refresh preview when flattening options change
  const debounceTimer = useRef(null);
  const isInitialMount = useRef(true);
  
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    if (step !== 3 || !response?.success) return;
    
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    debounceTimer.current = setTimeout(() => {
      handleFlattenPreview();
    }, 500);
    
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [flattenConfig.maxDepth, flattenConfig.arrayHandling, flattenConfig.separator, dataPath]);
  
  const updateParam = (list, setList, index, field, value) => {
    const updated = [...list];
    updated[index][field] = value;
    setList(updated);
    
    // Add new empty row if last row is being filled
    if (index === list.length - 1 && (updated[index].key || updated[index].value)) {
      setList([...updated, { key: '', value: '', enabled: true }]);
    }
  };
  
  const removeParam = (list, setList, index) => {
    if (list.length > 1) {
      setList(list.filter((_, i) => i !== index));
    }
  };
  
  const buildUrl = () => {
    let finalUrl = url;
    
    // Replace path params
    pathParams.filter(p => p.enabled && p.key && p.value).forEach(param => {
      finalUrl = finalUrl.replace(`:${param.key}`, param.value);
      finalUrl = finalUrl.replace(`{${param.key}}`, param.value);
    });
    
    // Add query params
    const enabledQueryParams = queryParams.filter(p => p.enabled && p.key);
    if (enabledQueryParams.length > 0) {
      const searchParams = new URLSearchParams();
      enabledQueryParams.forEach(p => searchParams.append(p.key, p.value));
      finalUrl += (finalUrl.includes('?') ? '&' : '?') + searchParams.toString();
    }
    
    return finalUrl;
  };
  
  const buildHeaders = () => {
    const result = {};
    headers.filter(h => h.enabled && h.key).forEach(h => {
      result[h.key] = h.value;
    });
    
    return result;
  };
  
  const handleTest = async () => {
    if (!url) {
      toast.error('Please enter a URL');
      return;
    }
    
    setIsTesting(true);
    setResponse(null);
    
    try {
      const testConfig = {
        method,
        url: buildUrl(),
        headers: buildHeaders(),
        body: ['POST', 'PUT', 'PATCH'].includes(method) ? bodyContent : undefined
      };
      
      const result = await api.post('/data-sources/test-api', testConfig);
      setResponse(result.data);
      
      if (result.data.success) {
        toast.success('API request successful!');
        
        // Move to data extraction step
        setStep(2);
        
        // Try to auto-detect data path
        if (result.data.data) {
          detectDataPath(result.data.data);
          
          // Auto-detect pagination fields
          const paginationFields = detectPaginationFields(result.data.data);
          if (paginationFields) {
            setDetectedPaginationFields(paginationFields);
          }
        }
      } else {
        toast.error(result.data.error || 'Request failed');
      }
    } catch (error) {
      console.error('API test error:', error);
      setResponse({
        success: false,
        status: error.response?.status || 0,
        error: error.response?.data?.error || error.message
      });
      toast.error('Failed to test API');
    } finally {
      setIsTesting(false);
    }
  };
  
  // Auto-detect common data path patterns
  const detectDataPath = (data) => {
    if (Array.isArray(data)) {
      setDataPath(''); // Root is array
      return;
    }
    
    // Common API patterns
    const commonPaths = ['data', 'results', 'items', 'records', 'rows', 'content', 'payload'];
    for (const path of commonPaths) {
      if (data[path] && Array.isArray(data[path])) {
        setDataPath(path);
        return;
      }
    }
    
    // Check nested patterns like data.results
    for (const key of Object.keys(data)) {
      if (data[key] && typeof data[key] === 'object') {
        for (const nestedKey of Object.keys(data[key])) {
          if (Array.isArray(data[key][nestedKey])) {
            setDataPath(`${key}.${nestedKey}`);
            return;
          }
        }
      }
    }
  };
  
  // Helper to get nested value from object using dot notation
  const getNestedValue = (obj, path) => {
    if (!path) return obj;
    return path.split('.').reduce((curr, key) => curr?.[key], obj);
  };
  
  // Auto-detect pagination metadata fields
  const [detectedPaginationFields, setDetectedPaginationFields] = useState(null);
  
  const detectPaginationFields = (data) => {
    if (!data || typeof data !== 'object') return null;
    
    const detected = {
      totalPath: null,
      hasMorePath: null,
      cursorPath: null
    };
    
    // Common patterns for total count
    const totalPatterns = ['total', 'totalCount', 'total_count', 'count', 'totalRecords', 'total_records', 'totalItems', 'total_items'];
    // Common patterns for hasMore boolean
    const hasMorePatterns = ['hasMore', 'has_more', 'hasNext', 'has_next', 'moreAvailable', 'more_available'];
    // Common patterns for next cursor
    const cursorPatterns = ['next_cursor', 'nextCursor', 'cursor', 'next', 'nextPageToken', 'next_page_token'];
    
    // Search in root and common nested paths
    const searchPaths = ['', 'meta', 'pagination', 'paging', 'page_info', 'pageInfo', '_meta'];
    
    for (const basePath of searchPaths) {
      const searchObj = basePath ? getNestedValue(data, basePath) : data;
      if (!searchObj || typeof searchObj !== 'object' || Array.isArray(searchObj)) continue;
      
      for (const [key, value] of Object.entries(searchObj)) {
        const fullPath = basePath ? `${basePath}.${key}` : key;
        
        // Check for total count (must be a number)
        if (!detected.totalPath && totalPatterns.some(p => key.toLowerCase() === p.toLowerCase())) {
          if (typeof value === 'number') {
            detected.totalPath = fullPath;
          }
        }
        
        // Check for hasMore boolean
        if (!detected.hasMorePath && hasMorePatterns.some(p => key.toLowerCase() === p.toLowerCase())) {
          if (typeof value === 'boolean' || value === 'true' || value === 'false') {
            detected.hasMorePath = fullPath;
          }
        }
        
        // Check for cursor (must be a string or null)
        if (!detected.cursorPath && cursorPatterns.some(p => key.toLowerCase() === p.toLowerCase())) {
          if (typeof value === 'string' || value === null) {
            detected.cursorPath = fullPath;
          }
        }
      }
    }
    
    // Only return if we found something useful
    if (detected.totalPath || detected.hasMorePath || detected.cursorPath) {
      return detected;
    }
    return null;
  };
  
  // Get the structure of the response for data path helper
  const getStructurePreview = (obj, path = '') => {
    const items = [];
    
    if (Array.isArray(obj)) {
      items.push({ path: path || '(root)', type: 'array', length: obj.length, isArray: true });
    } else if (obj && typeof obj === 'object') {
      for (const [key, value] of Object.entries(obj)) {
        const fullPath = path ? `${path}.${key}` : key;
        if (Array.isArray(value)) {
          items.push({ path: fullPath, type: 'array', length: value.length, isArray: true });
        } else if (value && typeof value === 'object') {
          items.push({ path: fullPath, type: 'object', keys: Object.keys(value).length });
          // Check one level deeper for arrays
          for (const [nestedKey, nestedValue] of Object.entries(value)) {
            if (Array.isArray(nestedValue)) {
              items.push({ path: `${fullPath}.${nestedKey}`, type: 'array', length: nestedValue.length, isArray: true });
            }
          }
        }
      }
    }
    
    return items;
  };
  
  const handleFlattenPreview = async () => {
    if (!response?.success) {
      toast.error('Please test the API first');
      return;
    }
    
    setIsFlattenLoading(true);
    
    try {
      const result = await api.post('/data-sources/api-flatten-preview', {
        url: buildUrl(),
        method,
        headers: buildHeaders(),
        body: ['POST', 'PUT', 'PATCH'].includes(method) ? bodyContent : undefined,
        authType,
        authConfig: authType !== 'none' ? authConfig : undefined,
        dataPath,
        flattenConfig,
        limit: 20
      });
      
      if (result.data.success) {
        setFlattenedPreview(result.data.flattenedData);
        setRawPreview(result.data.rawData);
        setResponseStructure(result.data.responseStructure);
        
        // Update columns and select all by default
        const newColumns = result.data.schema?.columns || [];
        setColumns(newColumns);
        setSelectedColumns(new Set(newColumns.map(c => c.name)));
        
        // Move to preview step
        setStep(3);
      } else {
        toast.error(result.data.error || 'Failed to process data');
      }
    } catch (error) {
      console.error('Flatten preview error:', error);
      toast.error(error.response?.data?.error || 'Failed to process API data');
    } finally {
      setIsFlattenLoading(false);
    }
  };
  
  const handleColumnToggle = (columnName) => {
    const updated = new Set(selectedColumns);
    if (updated.has(columnName)) {
      updated.delete(columnName);
    } else {
      updated.add(columnName);
    }
    setSelectedColumns(updated);
  };
  
  const handleSelectAllColumns = () => {
    if (selectedColumns.size === columns.length) {
      setSelectedColumns(new Set());
    } else {
      setSelectedColumns(new Set(columns.map(c => c.name)));
    }
  };
  
  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name for the data source');
      return;
    }
    
    if (!url) {
      toast.error('Please enter a URL');
      return;
    }
    
    if (selectedColumns.size === 0) {
      toast.error('Please select at least one column');
      return;
    }
    
    setIsCreating(true);
    
    try {
      // Get selected column schema
      const selectedColumnSchema = columns.filter(c => selectedColumns.has(c.name));
      
      const connectionConfig = {
        method,
        url,
        headers: buildHeaders(),
        queryParams: queryParams.filter(p => p.enabled && p.key),
        pathParams: pathParams.filter(p => p.enabled && p.key),
        authType,
        authConfig: authType !== 'none' ? authConfig : undefined,
        body: ['POST', 'PUT', 'PATCH'].includes(method) ? bodyContent : undefined,
        dataPath,
        flattenConfig,
        columns: Array.from(selectedColumns),
        pagination: paginationType !== 'none' ? {
          type: paginationType,
          ...paginationConfig
        } : { type: 'none' },
        deduplication: deduplicationKeys.length > 0 ? {
          enabled: true,
          keyColumns: deduplicationKeys,
          strategy: 'merge'
        } : undefined,
        incrementalConfig: incrementalEnabled ? {
          enabled: true,
          column: incrementalConfig.column,
          columnType: incrementalConfig.columnType,
          strategy: incrementalConfig.strategy,
          strategyConfig: {
            // Include only relevant fields for selected strategy
            ...(incrementalConfig.strategy === 'query' && {
              paramName: incrementalConfig.strategyConfig.paramName,
              format: incrementalConfig.strategyConfig.format || 'iso8601'
            }),
            ...(incrementalConfig.strategy === 'path' && {
              template: incrementalConfig.strategyConfig.template,
              placeholder: incrementalConfig.strategyConfig.placeholder,
              pathFormat: incrementalConfig.strategyConfig.pathFormat || 'iso8601'
            }),
            ...(incrementalConfig.strategy === 'header' && {
              headerName: incrementalConfig.strategyConfig.headerName,
              headerFormat: incrementalConfig.strategyConfig.headerFormat || 'iso8601'
            }),
            ...(incrementalConfig.strategy === 'body' && {
              paramPath: incrementalConfig.strategyConfig.paramPath
            })
          },
          // Legacy: include paramName at root for backward compatibility
          ...(incrementalConfig.strategy === 'query' && {
            paramName: incrementalConfig.strategyConfig.paramName
          })
        } : { enabled: false }
      };
      
      const result = await api.post('/data-sources', {
        name,
        type: 'api',
        companyId,
        connectionConfig,
        schemaInfo: {
          tables: [{
            name: 'api_data',
            columns: selectedColumnSchema
          }]
        }
      });
      
      onSuccess(result.data.dataSource);
    } catch (error) {
      console.error('Create error:', error);
      toast.error(error.response?.data?.error || 'Failed to create data source');
    } finally {
      setIsCreating(false);
    }
  };
  
  const tabs = [
    { id: 'params', name: 'Params' },
    { id: 'auth', name: 'Authorization' },
    { id: 'headers', name: 'Headers' },
    { id: 'body', name: 'Body', disabled: !['POST', 'PUT', 'PATCH'].includes(method) }
  ];
  
  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center gap-4">
        {[
          { num: 1, label: 'Configure API' },
          { num: 2, label: 'Data Extraction' },
          { num: 3, label: 'Preview & Columns' }
        ].map((s, i) => (
          <div key={s.num} className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm ${
              step >= s.num 
                ? 'bg-primary-600 text-white' 
                : 'bg-surface-200 dark:bg-surface-700 text-surface-500'
            }`}>
              {step > s.num ? <CheckCircleIcon className="w-5 h-5" /> : s.num}
            </div>
            <span className={`text-sm ${step >= s.num ? 'text-surface-900 dark:text-white' : 'text-surface-500'}`}>
              {s.label}
            </span>
            {i < 2 && <div className="w-12 h-px bg-surface-200 dark:bg-surface-700" />}
          </div>
        ))}
      </div>
      
      {/* Step 1: API Configuration */}
      {step === 1 && (
        <>
          {/* URL Bar */}
          <div className="card p-4">
            <div className="flex gap-2">
              {/* Method selector */}
              <div className="relative">
                <select
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  className={`appearance-none pl-3 pr-8 py-2.5 rounded-lg font-medium text-white cursor-pointer ${METHOD_COLORS[method]}`}
                >
                  {HTTP_METHODS.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-white pointer-events-none" />
              </div>
              
              {/* URL input */}
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="input flex-1 font-mono text-sm"
                placeholder="https://api.example.com/data"
              />
              
              {/* Send button */}
              <button
                onClick={handleTest}
                disabled={isTesting}
                className="btn-primary px-6"
              >
                {isTesting ? (
                  <ArrowPathIcon className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <PlayIcon className="w-5 h-5" />
                    Test
                  </>
                )}
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="card overflow-hidden">
            <div className="flex border-b border-surface-200 dark:border-surface-700">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => !tab.disabled && setActiveTab(tab.id)}
                  disabled={tab.disabled}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : tab.disabled
                        ? 'border-transparent text-surface-400 cursor-not-allowed'
                        : 'border-transparent text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white'
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </div>
            
            <div className="p-4">
              {/* Params Tab */}
              {activeTab === 'params' && (
                <div className="space-y-4">
                  {/* Query Params */}
                  <div>
                    <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                      Query Parameters
                    </h4>
                    <div className="space-y-2">
                      {queryParams.map((param, i) => (
                        <div key={i} className="flex gap-2 items-center">
                          <input
                            type="checkbox"
                            checked={param.enabled}
                            onChange={(e) => updateParam(queryParams, setQueryParams, i, 'enabled', e.target.checked)}
                            className="rounded"
                          />
                          <input
                            type="text"
                            value={param.key}
                            onChange={(e) => updateParam(queryParams, setQueryParams, i, 'key', e.target.value)}
                            className="input flex-1"
                            placeholder="Key"
                          />
                          <input
                            type="text"
                            value={param.value}
                            onChange={(e) => updateParam(queryParams, setQueryParams, i, 'value', e.target.value)}
                            className="input flex-1"
                            placeholder="Value"
                          />
                          <button
                            onClick={() => removeParam(queryParams, setQueryParams, i)}
                            className="p-2 text-surface-400 hover:text-red-500"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Path Params */}
                  <div>
                    <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                      Path Parameters <span className="text-surface-400 font-normal">(use :param or {'{param}'} in URL)</span>
                    </h4>
                    <div className="space-y-2">
                      {pathParams.map((param, i) => (
                        <div key={i} className="flex gap-2 items-center">
                          <input
                            type="checkbox"
                            checked={param.enabled}
                            onChange={(e) => updateParam(pathParams, setPathParams, i, 'enabled', e.target.checked)}
                            className="rounded"
                          />
                          <input
                            type="text"
                            value={param.key}
                            onChange={(e) => updateParam(pathParams, setPathParams, i, 'key', e.target.value)}
                            className="input flex-1"
                            placeholder="Parameter name"
                          />
                          <input
                            type="text"
                            value={param.value}
                            onChange={(e) => updateParam(pathParams, setPathParams, i, 'value', e.target.value)}
                            className="input flex-1"
                            placeholder="Value"
                          />
                          <button
                            onClick={() => removeParam(pathParams, setPathParams, i)}
                            className="p-2 text-surface-400 hover:text-red-500"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Auth Tab */}
              {activeTab === 'auth' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                      Authorization Type
                    </label>
                    <select
                      value={authType}
                      onChange={(e) => setAuthType(e.target.value)}
                      className="input"
                    >
                      {AUTH_TYPES.map(auth => (
                        <option key={auth.id} value={auth.id}>{auth.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  {authType === 'basic' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                          Username
                        </label>
                        <input
                          type="text"
                          value={authConfig.username}
                          onChange={(e) => setAuthConfig({ ...authConfig, username: e.target.value })}
                          className="input"
                          placeholder="Username"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                          Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={authConfig.password}
                            onChange={(e) => setAuthConfig({ ...authConfig, password: e.target.value })}
                            className="input pr-10"
                            placeholder="Password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400"
                          >
                            {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {authType === 'bearer' && (
                    <div>
                      <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                        Token
                      </label>
                      <input
                        type="text"
                        value={authConfig.token}
                        onChange={(e) => setAuthConfig({ ...authConfig, token: e.target.value })}
                        className="input font-mono text-sm"
                        placeholder="Enter your token"
                      />
                    </div>
                  )}
                  
                  {authType === 'api_key' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                            Key Name
                          </label>
                          <input
                            type="text"
                            value={authConfig.apiKeyName}
                            onChange={(e) => setAuthConfig({ ...authConfig, apiKeyName: e.target.value })}
                            className="input"
                            placeholder="X-API-Key"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                            Key Value
                          </label>
                          <input
                            type="text"
                            value={authConfig.apiKeyValue}
                            onChange={(e) => setAuthConfig({ ...authConfig, apiKeyValue: e.target.value })}
                            className="input font-mono text-sm"
                            placeholder="your-api-key"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                          Add to
                        </label>
                        <select
                          value={authConfig.apiKeyIn}
                          onChange={(e) => setAuthConfig({ ...authConfig, apiKeyIn: e.target.value })}
                          className="input"
                        >
                          <option value="header">Header</option>
                          <option value="query">Query Params</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Headers Tab */}
              {activeTab === 'headers' && (
                <div className="space-y-2">
                  {headers.map((header, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input
                        type="checkbox"
                        checked={header.enabled}
                        onChange={(e) => updateParam(headers, setHeaders, i, 'enabled', e.target.checked)}
                        className="rounded"
                      />
                      <input
                        type="text"
                        value={header.key}
                        onChange={(e) => updateParam(headers, setHeaders, i, 'key', e.target.value)}
                        className="input flex-1"
                        placeholder="Header name"
                      />
                      <input
                        type="text"
                        value={header.value}
                        onChange={(e) => updateParam(headers, setHeaders, i, 'value', e.target.value)}
                        className="input flex-1"
                        placeholder="Value"
                      />
                      <button
                        onClick={() => removeParam(headers, setHeaders, i)}
                        className="p-2 text-surface-400 hover:text-red-500"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Body Tab */}
              {activeTab === 'body' && (
                <div className="space-y-4">
                  <div className="flex gap-4">
                    {['json', 'text', 'form-data'].map(type => (
                      <label key={type} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="bodyType"
                          value={type}
                          checked={bodyType === type}
                          onChange={(e) => setBodyType(e.target.value)}
                          className="text-primary-600"
                        />
                        <span className="text-sm text-surface-700 dark:text-surface-300 capitalize">
                          {type === 'form-data' ? 'Form Data' : type.toUpperCase()}
                        </span>
                      </label>
                    ))}
                  </div>
                  
                  <textarea
                    value={bodyContent}
                    onChange={(e) => setBodyContent(e.target.value)}
                    className="input font-mono text-sm h-48 resize-none"
                    placeholder={bodyType === 'json' ? '{\n  "key": "value"\n}' : 'Enter body content...'}
                  />
                </div>
              )}
            </div>
          </div>
          
          {/* Response Section (Step 1) */}
          {response && (
            <div className="card overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-surface-200 dark:border-surface-700">
                <div className="flex items-center gap-3">
                  {response.success ? (
                    <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <ExclamationCircleIcon className="w-5 h-5 text-red-500" />
                  )}
                  <span className="font-medium text-surface-900 dark:text-white">
                    Response
                  </span>
                  <span className={`text-sm px-2 py-0.5 rounded ${
                    response.status >= 200 && response.status < 300
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                  }`}>
                    {response.status}
                  </span>
                  {response.duration && (
                    <span className="text-sm text-surface-500">{response.duration}ms</span>
                  )}
                </div>
              </div>
              
              <div className="p-4">
                {response.error ? (
                  <div className="text-red-600 dark:text-red-400">{response.error}</div>
                ) : (
                  <pre className="text-sm text-surface-700 dark:text-surface-300 overflow-x-auto bg-surface-50 dark:bg-surface-800 p-4 rounded-lg max-h-64">
                    {JSON.stringify(response.data, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          )}
          
          {/* Action buttons */}
          <div className="flex items-center justify-between">
            <button onClick={onBack} className="btn-secondary">
              Back
            </button>
            {response?.success && (
              <button onClick={() => setStep(2)} className="btn-primary">
                <ChevronRightIcon className="w-5 h-5" />
                Configure Data Extraction
              </button>
            )}
          </div>
        </>
      )}
      
      {/* Step 2: Data Extraction Configuration */}
      {step === 2 && (
        <>
          {/* Data Path Configuration */}
          <div className="card p-4">
            <div className="flex items-center gap-2 mb-4">
              <DocumentMagnifyingGlassIcon className="w-5 h-5 text-surface-400" />
              <h4 className="font-medium text-surface-900 dark:text-white">
                Data Path
              </h4>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  JSON Path to Data Array
                </label>
                <input
                  type="text"
                  value={dataPath}
                  onChange={(e) => setDataPath(e.target.value)}
                  className="input font-mono text-sm"
                  placeholder="e.g., data, results, data.items (leave empty for root array)"
                />
                <p className="text-sm text-surface-500 mt-2">
                  Specify the path to the array of records in the API response. Use dot notation for nested paths.
                </p>
              </div>
              
              {/* Show detected array paths from response */}
              {response?.data && (
                <div>
                  <p className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                    Detected Arrays in Response:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {getStructurePreview(response.data).filter(s => s.isArray).map((item, i) => (
                      <button
                        key={i}
                        onClick={() => setDataPath(item.path === '(root)' ? '' : item.path)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-mono transition-colors ${
                          (dataPath === '' && item.path === '(root)') || dataPath === item.path
                            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 border border-primary-300 dark:border-primary-700'
                            : 'bg-surface-100 dark:bg-surface-800 text-surface-700 dark:text-surface-300 hover:bg-surface-200 dark:hover:bg-surface-700'
                        }`}
                      >
                        {item.path} <span className="text-surface-500">({item.length} items)</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Flattening Options */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AdjustmentsHorizontalIcon className="w-5 h-5 text-surface-400" />
                <h4 className="font-medium text-surface-900 dark:text-white">
                  Flattening Options
                </h4>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  Max Nesting Depth
                </label>
                <input
                  type="number"
                  value={flattenConfig.maxDepth}
                  onChange={(e) => setFlattenConfig({ ...flattenConfig, maxDepth: parseInt(e.target.value) })}
                  className="input"
                  min={1}
                  max={10}
                />
                <p className="text-xs text-surface-500 mt-1">
                  How deep to flatten nested objects
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  Array Handling
                </label>
                <select
                  value={flattenConfig.arrayHandling}
                  onChange={(e) => setFlattenConfig({ ...flattenConfig, arrayHandling: e.target.value })}
                  className="input"
                >
                  <option value="first">Take first element</option>
                  <option value="stringify">Convert to JSON string</option>
                  <option value="join">Join values with comma</option>
                </select>
                <p className="text-xs text-surface-500 mt-1">
                  How to handle array values
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  Key Separator
                </label>
                <input
                  type="text"
                  value={flattenConfig.separator}
                  onChange={(e) => setFlattenConfig({ ...flattenConfig, separator: e.target.value })}
                  className="input w-24"
                  placeholder="_"
                />
                <p className="text-xs text-surface-500 mt-1">
                  Separator for nested keys (e.g., address_city)
                </p>
              </div>
            </div>
          </div>
          
          {/* Pagination Configuration */}
          <div className="card p-4">
            <div className="flex items-center gap-2 mb-4">
              <TableCellsIcon className="w-5 h-5 text-surface-400" />
              <h4 className="font-medium text-surface-900 dark:text-white">
                Pagination
              </h4>
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                For syncing
              </span>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  Pagination Type
                </label>
                <select
                  value={paginationType}
                  onChange={(e) => setPaginationType(e.target.value)}
                  className="input"
                >
                  {PAGINATION_TYPES.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <p className="text-sm text-surface-500 mt-1">
                  {PAGINATION_TYPES.find(p => p.id === paginationType)?.description}
                </p>
              </div>
              
              {paginationType !== 'none' && (
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-surface-200 dark:border-surface-700">
                  {(paginationType === 'page' || paginationType === 'offset') && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                          {paginationType === 'page' ? 'Page Parameter' : 'Offset Parameter'}
                        </label>
                        <input
                          type="text"
                          value={paginationConfig.pageParam}
                          onChange={(e) => setPaginationConfig({ ...paginationConfig, pageParam: e.target.value })}
                          className="input"
                          placeholder={paginationType === 'page' ? 'page' : 'offset'}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                          Limit Parameter
                        </label>
                        <input
                          type="text"
                          value={paginationConfig.limitParam}
                          onChange={(e) => setPaginationConfig({ ...paginationConfig, limitParam: e.target.value })}
                          className="input"
                          placeholder="limit"
                        />
                      </div>
                      {/* Auto-detected pagination fields banner */}
                      {detectedPaginationFields && (detectedPaginationFields.totalPath || detectedPaginationFields.hasMorePath) && (
                        <div className="col-span-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-green-600 dark:text-green-400">✨</span>
                              <span className="text-sm font-medium text-green-800 dark:text-green-200">
                                Pagination fields detected in API response
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setPaginationConfig({
                                  ...paginationConfig,
                                  totalPath: detectedPaginationFields.totalPath || paginationConfig.totalPath,
                                  hasMorePath: detectedPaginationFields.hasMorePath || paginationConfig.hasMorePath
                                });
                                toast.success('Applied detected pagination fields');
                              }}
                              className="text-xs font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-800/50 px-3 py-1 rounded-full hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
                            >
                              Apply Detected Fields
                            </button>
                          </div>
                          <div className="mt-2 text-xs text-green-700 dark:text-green-300 space-y-1">
                            {detectedPaginationFields.totalPath && (
                              <p>• Total count: <code className="bg-green-100 dark:bg-green-800/50 px-1 rounded">{detectedPaginationFields.totalPath}</code></p>
                            )}
                            {detectedPaginationFields.hasMorePath && (
                              <p>• Has more: <code className="bg-green-100 dark:bg-green-800/50 px-1 rounded">{detectedPaginationFields.hasMorePath}</code></p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div>
                        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                          Total Count Path
                          <span className="text-xs text-surface-400 ml-1">(recommended)</span>
                        </label>
                        <input
                          type="text"
                          value={paginationConfig.totalPath}
                          onChange={(e) => setPaginationConfig({ ...paginationConfig, totalPath: e.target.value })}
                          className="input font-mono text-sm"
                          placeholder="total, meta.total_count"
                        />
                        <p className="text-xs text-surface-500 mt-1">
                          JSON path to total record count (e.g., "total" for {"{ total: 194 }"})
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                          Has More Path
                          <span className="text-xs text-surface-400 ml-1">(optional)</span>
                        </label>
                        <input
                          type="text"
                          value={paginationConfig.hasMorePath}
                          onChange={(e) => setPaginationConfig({ ...paginationConfig, hasMorePath: e.target.value })}
                          className="input font-mono text-sm"
                          placeholder="has_more, meta.has_next"
                        />
                        <p className="text-xs text-surface-500 mt-1">
                          JSON path to boolean indicating more data exists
                        </p>
                      </div>
                    </>
                  )}
                  
                  {paginationType === 'cursor' && (
                    <>
                      {/* Auto-detected cursor pagination fields */}
                      {detectedPaginationFields && (detectedPaginationFields.cursorPath || detectedPaginationFields.hasMorePath) && (
                        <div className="col-span-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-green-600 dark:text-green-400">✨</span>
                              <span className="text-sm font-medium text-green-800 dark:text-green-200">
                                Cursor fields detected in API response
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setPaginationConfig({
                                  ...paginationConfig,
                                  cursorPath: detectedPaginationFields.cursorPath || paginationConfig.cursorPath,
                                  hasMorePath: detectedPaginationFields.hasMorePath || paginationConfig.hasMorePath
                                });
                                toast.success('Applied detected cursor fields');
                              }}
                              className="text-xs font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-800/50 px-3 py-1 rounded-full hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
                            >
                              Apply Detected Fields
                            </button>
                          </div>
                          <div className="mt-2 text-xs text-green-700 dark:text-green-300 space-y-1">
                            {detectedPaginationFields.cursorPath && (
                              <p>• Next cursor: <code className="bg-green-100 dark:bg-green-800/50 px-1 rounded">{detectedPaginationFields.cursorPath}</code></p>
                            )}
                            {detectedPaginationFields.hasMorePath && (
                              <p>• Has more: <code className="bg-green-100 dark:bg-green-800/50 px-1 rounded">{detectedPaginationFields.hasMorePath}</code></p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div>
                        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                          Cursor Parameter
                        </label>
                        <input
                          type="text"
                          value={paginationConfig.cursorParam}
                          onChange={(e) => setPaginationConfig({ ...paginationConfig, cursorParam: e.target.value })}
                          className="input"
                          placeholder="cursor"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                          Next Cursor Path
                        </label>
                        <input
                          type="text"
                          value={paginationConfig.cursorPath}
                          onChange={(e) => setPaginationConfig({ ...paginationConfig, cursorPath: e.target.value })}
                          className="input font-mono text-sm"
                          placeholder="meta.next_cursor"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                          Has More Path (optional)
                        </label>
                        <input
                          type="text"
                          value={paginationConfig.hasMorePath}
                          onChange={(e) => setPaginationConfig({ ...paginationConfig, hasMorePath: e.target.value })}
                          className="input font-mono text-sm"
                          placeholder="meta.has_more"
                        />
                      </div>
                    </>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                      Page Size
                    </label>
                    <input
                      type="number"
                      value={paginationConfig.limit}
                      onChange={(e) => setPaginationConfig({ ...paginationConfig, limit: parseInt(e.target.value) })}
                      className="input"
                      min={1}
                      max={10000}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                      Max Pages
                    </label>
                    <input
                      type="number"
                      value={paginationConfig.maxPages}
                      onChange={(e) => setPaginationConfig({ ...paginationConfig, maxPages: parseInt(e.target.value) })}
                      className="input"
                      min={1}
                      max={100000}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Incremental Sync Configuration */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ClockIcon className="w-5 h-5 text-surface-400" />
                <h4 className="font-medium text-surface-900 dark:text-white">
                  Incremental Sync
                </h4>
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                  Optional
                </span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={incrementalEnabled}
                  onChange={(e) => setIncrementalEnabled(e.target.checked)}
                  className="rounded text-primary-600"
                />
                <span className="text-sm text-surface-700 dark:text-surface-300">
                  Enable
                </span>
              </label>
            </div>

            {!incrementalEnabled ? (
              <p className="text-sm text-surface-500">
                Enable incremental sync to only fetch new or updated records on subsequent syncs,
                instead of re-fetching all data every time.
              </p>
            ) : (
              <div className="space-y-6">
                {/* Strategy Selection */}
                <div>
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-3">
                    Sync Strategy
                  </label>
                  <div className="grid grid-cols-1 gap-3">
                    {INCREMENTAL_STRATEGIES.map((strategy) => {
                      const isDisabled = strategy.requiresCursor && paginationType !== 'cursor';
                      return (
                        <button
                          key={strategy.id}
                          type="button"
                          onClick={() => {
                            if (!isDisabled) {
                              setIncrementalConfig({
                                ...incrementalConfig,
                                strategy: strategy.id
                              });
                            }
                          }}
                          disabled={isDisabled}
                          className={`${
                            incrementalConfig.strategy === strategy.id
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                              : isDisabled
                              ? 'border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800/50 opacity-60 cursor-not-allowed'
                              : 'border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800'
                          } relative flex items-start p-4 border rounded-lg ${!isDisabled ? 'hover:border-primary-400 dark:hover:border-primary-600' : ''} transition-colors text-left`}
                        >
                          <div className="flex items-center h-5">
                            <input
                              type="radio"
                              checked={incrementalConfig.strategy === strategy.id}
                              onChange={() => {}}
                              disabled={isDisabled}
                              className="h-4 w-4 text-primary-600 border-surface-300"
                            />
                          </div>
                          <div className="ml-3 flex-1">
                            <div className={`text-sm font-medium ${isDisabled ? 'text-surface-500' : 'text-surface-900 dark:text-white'}`}>
                              {strategy.name}
                              {isDisabled && (
                                <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded">
                                  Requires cursor pagination
                                </span>
                              )}
                            </div>
                            <div className={`text-xs mt-1 ${isDisabled ? 'text-surface-400' : 'text-surface-500'}`}>
                              {strategy.description}
                            </div>
                            <div className={`text-xs mt-1 font-mono ${isDisabled ? 'text-surface-300' : 'text-surface-400'}`}>
                              {strategy.example}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Common Fields: Tracking Column */}
                <div>
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                    Tracking Column
                  </label>
                  <input
                    type="text"
                    value={incrementalConfig.column}
                    onChange={(e) =>
                      setIncrementalConfig({
                        ...incrementalConfig,
                        column: e.target.value
                      })
                    }
                    className="input font-mono text-sm w-full"
                    placeholder="updated_at, modified_date, id, etc."
                  />
                  <p className="text-xs text-surface-500 mt-1">
                    The field in the API response that tracks when records change
                  </p>
                </div>

                {/* Common Fields: Column Type */}
                <div>
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                    Column Data Type
                  </label>
                  <select
                    value={incrementalConfig.columnType}
                    onChange={(e) =>
                      setIncrementalConfig({
                        ...incrementalConfig,
                        columnType: e.target.value
                      })
                    }
                    className="input w-full"
                  >
                    <option value="auto">Auto-detect</option>
                    <option value="datetime">DateTime (ISO timestamp)</option>
                    <option value="date">Date (YYYY-MM-DD)</option>
                    <option value="number">Number (ID, sequence)</option>
                    <option value="string">String (alphabetical)</option>
                  </select>
                </div>

                {/* Strategy-Specific Fields */}
                {incrementalConfig.strategy === 'query' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                        API Parameter Name
                      </label>
                      <input
                        type="text"
                        value={incrementalConfig.strategyConfig.paramName}
                        onChange={(e) =>
                          setIncrementalConfig({
                            ...incrementalConfig,
                            strategyConfig: {
                              ...incrementalConfig.strategyConfig,
                              paramName: e.target.value
                            }
                          })
                        }
                        className="input font-mono text-sm w-full"
                        placeholder="updated_since, modified_after, etc."
                      />
                      <p className="text-xs text-surface-500 mt-1">
                        The query parameter name that your API uses for filtering
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                        Date/Time Format
                      </label>
                      <select
                        value={incrementalConfig.strategyConfig.format || 'iso8601'}
                        onChange={(e) =>
                          setIncrementalConfig({
                            ...incrementalConfig,
                            strategyConfig: {
                              ...incrementalConfig.strategyConfig,
                              format: e.target.value
                            }
                          })
                        }
                        className="input w-full"
                      >
                        {DATE_FORMATS.map((format) => (
                          <option key={format.id} value={format.id}>
                            {format.name} - {format.example}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-surface-500 mt-1">
                        {DATE_FORMATS.find(f => f.id === (incrementalConfig.strategyConfig.format || 'iso8601'))?.description}
                      </p>
                      {/* Live preview */}
                      <div className="mt-2 p-2 bg-surface-100 dark:bg-surface-700 rounded">
                        <span className="text-xs text-surface-500">Live preview: </span>
                        <code className="text-xs text-primary-600 dark:text-primary-400">
                          {formatDatePreview(incrementalConfig.strategyConfig.format || 'iso8601')}
                        </code>
                      </div>
                    </div>
                  </div>
                )}

                {incrementalConfig.strategy === 'path' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                        URL Template
                      </label>
                      <input
                        type="text"
                        value={incrementalConfig.strategyConfig.template}
                        onChange={(e) =>
                          setIncrementalConfig({
                            ...incrementalConfig,
                            strategyConfig: {
                              ...incrementalConfig.strategyConfig,
                              template: e.target.value
                            }
                          })
                        }
                        className="input font-mono text-sm w-full"
                        placeholder="/api/events/{lastDate}/latest"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                        Placeholder Name
                      </label>
                      <input
                        type="text"
                        value={incrementalConfig.strategyConfig.placeholder}
                        onChange={(e) =>
                          setIncrementalConfig({
                            ...incrementalConfig,
                            strategyConfig: {
                              ...incrementalConfig.strategyConfig,
                              placeholder: e.target.value
                            }
                          })
                        }
                        className="input w-full"
                        placeholder="lastDate"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                        Date/Time Format
                      </label>
                      <select
                        value={incrementalConfig.strategyConfig.pathFormat || 'iso8601'}
                        onChange={(e) =>
                          setIncrementalConfig({
                            ...incrementalConfig,
                            strategyConfig: {
                              ...incrementalConfig.strategyConfig,
                              pathFormat: e.target.value
                            }
                          })
                        }
                        className="input w-full"
                      >
                        {DATE_FORMATS.map((format) => (
                          <option key={format.id} value={format.id}>
                            {format.name} - {format.example}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-surface-500 mt-1">
                        {DATE_FORMATS.find(f => f.id === (incrementalConfig.strategyConfig.pathFormat || 'iso8601'))?.description}
                      </p>
                      {/* Live preview */}
                      <div className="mt-2 p-2 bg-surface-100 dark:bg-surface-700 rounded">
                        <span className="text-xs text-surface-500">Live preview: </span>
                        <code className="text-xs text-primary-600 dark:text-primary-400">
                          {formatDatePreview(incrementalConfig.strategyConfig.pathFormat || 'iso8601')}
                        </code>
                      </div>
                    </div>
                  </div>
                )}

                {incrementalConfig.strategy === 'header' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                        Header Name
                      </label>
                      <input
                        type="text"
                        value={incrementalConfig.strategyConfig.headerName}
                        onChange={(e) =>
                          setIncrementalConfig({
                            ...incrementalConfig,
                            strategyConfig: {
                              ...incrementalConfig.strategyConfig,
                              headerName: e.target.value
                            }
                          })
                        }
                        className="input w-full"
                        placeholder="If-Modified-Since"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                        Date/Time Format
                      </label>
                      <select
                        value={incrementalConfig.strategyConfig.headerFormat || 'iso8601'}
                        onChange={(e) =>
                          setIncrementalConfig({
                            ...incrementalConfig,
                            strategyConfig: {
                              ...incrementalConfig.strategyConfig,
                              headerFormat: e.target.value
                            }
                          })
                        }
                        className="input w-full"
                      >
                        {DATE_FORMATS.map((format) => (
                          <option key={format.id} value={format.id}>
                            {format.name} - {format.example}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-surface-500 mt-1">
                        {DATE_FORMATS.find(f => f.id === (incrementalConfig.strategyConfig.headerFormat || 'iso8601'))?.description}
                      </p>
                      {/* Live preview */}
                      <div className="mt-2 p-2 bg-surface-100 dark:bg-surface-700 rounded">
                        <span className="text-xs text-surface-500">Live preview: </span>
                        <code className="text-xs text-primary-600 dark:text-primary-400">
                          {incrementalConfig.strategyConfig.headerName || 'If-Modified-Since'}: {formatDatePreview(incrementalConfig.strategyConfig.headerFormat || 'iso8601')}
                        </code>
                      </div>
                    </div>
                  </div>
                )}

                {incrementalConfig.strategy === 'body' && (
                  <div>
                    {method === 'GET' && (
                      <div className="p-3 mb-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                        <p className="text-sm text-amber-700 dark:text-amber-400">
                          <strong>⚠ Warning:</strong> Body Parameter strategy cannot be used with GET requests.
                          Please change your HTTP method to POST, PUT, or PATCH, or select a different sync strategy.
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                        Parameter Path (dot notation)
                      </label>
                      <input
                        type="text"
                        value={incrementalConfig.strategyConfig.paramPath}
                        onChange={(e) =>
                          setIncrementalConfig({
                            ...incrementalConfig,
                            strategyConfig: {
                              ...incrementalConfig.strategyConfig,
                              paramPath: e.target.value
                            }
                          })
                        }
                        className="input font-mono text-sm w-full"
                        placeholder="filter.updatedAfter"
                        disabled={method === 'GET'}
                      />
                      <p className="text-xs text-surface-500 mt-1">
                        Nested path in request body where the filter value will be placed
                      </p>
                    </div>
                  </div>
                )}

                {incrementalConfig.strategy === 'cursor' && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-blue-700 dark:text-blue-400">
                      <strong>Cursor + State Strategy</strong>
                      <br />
                      <span className="mt-1 block">
                        This strategy tracks the incremental value (e.g., updated_at) alongside cursor pagination.
                        The cursor handles fetching, while the tracked value enables resume capability and deduplication.
                      </span>
                      <br />
                      <span className="mt-1 block">
                        No additional configuration needed - just ensure cursor pagination is configured above.
                      </span>
                    </p>
                  </div>
                )}

                {/* URL Preview with incremental filter */}
                {incrementalConfig.strategy && incrementalConfig.strategy !== 'cursor' && (
                  <div className="mt-4 p-3 bg-surface-50 dark:bg-surface-800 rounded-lg border border-surface-200 dark:border-surface-700">
                    <h5 className="text-xs font-medium text-surface-500 uppercase tracking-wider mb-2">
                      URL Preview (during incremental sync)
                    </h5>
                    <div className="font-mono text-xs break-all">
                      {(() => {
                        // Build preview URL based on strategy
                        let previewUrl = url || 'https://api.example.com/data';
                        const exampleValue = '2024-01-15T10:30:00Z';
                        
                        if (incrementalConfig.strategy === 'query') {
                          const paramName = incrementalConfig.strategyConfig.paramName || 'updated_since';
                          const separator = previewUrl.includes('?') ? '&' : '?';
                          return (
                            <span>
                              <span className="text-surface-600 dark:text-surface-400">{url || 'https://api.example.com/data'}</span>
                              <span className="text-primary-600 dark:text-primary-400 font-semibold">
                                {separator}{paramName}={exampleValue}
                              </span>
                            </span>
                          );
                        }
                        
                        if (incrementalConfig.strategy === 'path') {
                          const template = incrementalConfig.strategyConfig.template || '/data/{date}';
                          const placeholder = incrementalConfig.strategyConfig.placeholder || '{date}';
                          const baseUrl = url ? url.split('/').slice(0, 3).join('/') : 'https://api.example.com';
                          const previewPath = template.replace(placeholder, exampleValue);
                          return (
                            <span>
                              <span className="text-surface-600 dark:text-surface-400">{baseUrl}</span>
                              <span className="text-primary-600 dark:text-primary-400 font-semibold">{previewPath}</span>
                            </span>
                          );
                        }
                        
                        if (incrementalConfig.strategy === 'header') {
                          const headerName = incrementalConfig.strategyConfig.headerName || 'If-Modified-Since';
                          return (
                            <div>
                              <span className="text-surface-600 dark:text-surface-400">{url || 'https://api.example.com/data'}</span>
                              <div className="mt-2 pt-2 border-t border-surface-200 dark:border-surface-700">
                                <span className="text-surface-500">Header: </span>
                                <span className="text-primary-600 dark:text-primary-400 font-semibold">
                                  {headerName}: {exampleValue}
                                </span>
                              </div>
                            </div>
                          );
                        }
                        
                        if (incrementalConfig.strategy === 'body') {
                          const paramPath = incrementalConfig.strategyConfig.paramPath || 'filter.updatedAfter';
                          // Build proper nested JSON preview
                          const parts = paramPath.split('.');
                          let bodyPreview = '';
                          for (let i = 0; i < parts.length; i++) {
                            bodyPreview += `${'  '.repeat(i)}${i > 0 ? '' : ''}{ "${parts[i]}": `;
                          }
                          bodyPreview += `"${exampleValue}"`;
                          for (let i = parts.length - 1; i >= 0; i--) {
                            bodyPreview += ` }`;
                          }
                          return (
                            <div>
                              <span className="text-surface-600 dark:text-surface-400">{url || 'https://api.example.com/data'}</span>
                              <div className="mt-2 pt-2 border-t border-surface-200 dark:border-surface-700">
                                <span className="text-surface-500">Request body: </span>
                                <span className="text-primary-600 dark:text-primary-400 font-semibold">
                                  {bodyPreview}
                                </span>
                              </div>
                            </div>
                          );
                        }
                        
                        return <span className="text-surface-600 dark:text-surface-400">{previewUrl}</span>;
                      })()}
                    </div>
                    <p className="text-xs text-surface-400 mt-2">
                      Example showing how the incremental filter will be applied. Actual value will be the last synced timestamp.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Action buttons */}
          <div className="flex items-center justify-between">
            <button onClick={() => setStep(1)} className="btn-secondary">
              Back
            </button>
            <button 
              onClick={handleFlattenPreview}
              disabled={isFlattenLoading}
              className="btn-primary"
            >
              {isFlattenLoading ? (
                <>
                  <ArrowPathIcon className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <PlayIcon className="w-5 h-5" />
                  Preview Data
                </>
              )}
            </button>
          </div>
        </>
      )}
      
      {/* Step 3: Preview & Column Selection */}
      {step === 3 && (
        <>
          {/* Name input */}
          <div className="card p-4">
            <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
              Data Source Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder="My API Data Source"
            />
          </div>
          
          {/* Flattening Options (adjustable) */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AdjustmentsHorizontalIcon className="w-5 h-5 text-surface-400" />
                <h4 className="font-medium text-surface-900 dark:text-white">
                  Flattening Options
                </h4>
              </div>
              <button
                onClick={handleFlattenPreview}
                disabled={isFlattenLoading}
                className="btn-secondary text-sm"
              >
                {isFlattenLoading ? (
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                ) : (
                  'Update Preview'
                )}
              </button>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  Max Nesting Depth
                </label>
                <input
                  type="number"
                  value={flattenConfig.maxDepth}
                  onChange={(e) => setFlattenConfig({ ...flattenConfig, maxDepth: parseInt(e.target.value) })}
                  className="input"
                  min={1}
                  max={10}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  Array Handling
                </label>
                <select
                  value={flattenConfig.arrayHandling}
                  onChange={(e) => setFlattenConfig({ ...flattenConfig, arrayHandling: e.target.value })}
                  className="input"
                >
                  <option value="first">Take first element</option>
                  <option value="stringify">Convert to JSON string</option>
                  <option value="join">Join values with comma</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  Key Separator
                </label>
                <input
                  type="text"
                  value={flattenConfig.separator}
                  onChange={(e) => setFlattenConfig({ ...flattenConfig, separator: e.target.value })}
                  className="input w-24"
                />
              </div>
            </div>
          </div>
          
          {/* Column Selection */}
          {columns.length > 0 && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-medium text-surface-900 dark:text-white">
                    Select Columns ({selectedColumns.size} of {columns.length})
                  </h4>
                  <p className="text-sm text-surface-500 mt-1">
                    Choose which flattened fields to include in your data source
                  </p>
                </div>
                <button
                  onClick={handleSelectAllColumns}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {selectedColumns.size === columns.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              
              {/* Column Search */}
              {columns.length > 8 && (
                <div className="relative mb-3">
                  <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                  <input
                    type="text"
                    value={columnSearchTerm}
                    onChange={(e) => setColumnSearchTerm(e.target.value)}
                    placeholder="Search columns..."
                    className="input pl-9 py-2 text-sm w-full"
                  />
                </div>
              )}
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                {columns
                  .filter(col => col.name.toLowerCase().includes(columnSearchTerm.toLowerCase()))
                  .map((col) => (
                  <label
                    key={col.name}
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedColumns.has(col.name)
                        ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800'
                        : 'bg-surface-50 dark:bg-surface-800 border border-transparent hover:bg-surface-100 dark:hover:bg-surface-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedColumns.has(col.name)}
                      onChange={() => handleColumnToggle(col.name)}
                      className="rounded text-primary-600"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-surface-900 dark:text-white truncate" title={col.name}>
                        {col.name}
                      </p>
                      <p className="text-xs text-surface-500">{col.type}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
          
          {/* Deduplication Key Selection */}
          {columns.length > 0 && selectedColumns.size > 0 && (
            <div className="card p-4">
              <div className="mb-4">
                <h4 className="font-medium text-surface-900 dark:text-white flex items-center gap-2">
                  Deduplication Keys
                  <span className="text-xs font-normal text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded">
                    Recommended
                  </span>
                </h4>
                <p className="text-sm text-surface-500 mt-1">
                  Select unique identifier column(s) to prevent duplicate records during sync (e.g., "id", "uuid"). 
                  Used for MERGE operations in BigQuery.
                </p>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {columns.filter(c => selectedColumns.has(c.name)).map((col) => (
                  <label
                    key={col.name}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors text-sm ${
                      deduplicationKeys.includes(col.name)
                        ? 'bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200'
                        : 'bg-surface-100 dark:bg-surface-700 border border-transparent hover:bg-surface-200 dark:hover:bg-surface-600'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={deduplicationKeys.includes(col.name)}
                      onChange={() => {
                        if (deduplicationKeys.includes(col.name)) {
                          setDeduplicationKeys(deduplicationKeys.filter(k => k !== col.name));
                        } else {
                          setDeduplicationKeys([...deduplicationKeys, col.name]);
                        }
                      }}
                      className="rounded text-amber-600"
                    />
                    <span className="font-mono">{col.name}</span>
                    <span className="text-xs text-surface-400">({col.type})</span>
                  </label>
                ))}
              </div>
              
              {deduplicationKeys.length === 0 && (
                <div className="mt-3 p-3 bg-surface-50 dark:bg-surface-800 rounded-lg border border-surface-200 dark:border-surface-700">
                  <p className="text-sm text-surface-600 dark:text-surface-400">
                    <span className="font-medium">Tip:</span> If your API returns an "id" field, selecting it as a deduplication key will 
                    ensure incremental syncs don't create duplicate records.
                  </p>
                </div>
              )}
              
              {deduplicationKeys.length > 0 && (
                <div className="mt-3 text-sm text-surface-600 dark:text-surface-400">
                  Merge key: <code className="bg-surface-100 dark:bg-surface-800 px-2 py-0.5 rounded font-mono">
                    {deduplicationKeys.join(' + ')}
                  </code>
                </div>
              )}
            </div>
          )}
          
          {/* Data Preview */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-surface-900 dark:text-white">
                Data Preview (Flattened)
              </h4>
              <span className="text-sm text-surface-500">
                Showing {selectedColumns.size} of {columns.length} columns
              </span>
            </div>
            
            {isFlattenLoading ? (
              <div className="flex items-center justify-center py-8">
                <ArrowPathIcon className="w-8 h-8 text-primary-500 animate-spin" />
              </div>
            ) : selectedColumns.size === 0 ? (
              <div className="text-center py-8 text-surface-500">
                Please select at least one column to preview
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-surface-200 dark:border-surface-700">
                <table className="w-full text-sm">
                  <thead className="bg-surface-50 dark:bg-surface-800">
                    <tr>
                      {columns.filter(c => selectedColumns.has(c.name)).map((col, i) => (
                        <th key={i} className="px-4 py-2 text-left font-medium text-surface-600 dark:text-surface-400 whitespace-nowrap">
                          {col.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {flattenedPreview?.slice(0, 10).map((row, i) => (
                      <tr key={i} className="border-t border-surface-100 dark:border-surface-800">
                        {columns.filter(c => selectedColumns.has(c.name)).map((col, j) => (
                          <td key={j} className="px-4 py-2 text-surface-700 dark:text-surface-300 whitespace-nowrap max-w-xs truncate" title={String(row[col.name] ?? '')}>
                            {String(row[col.name] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* Raw Data Preview (collapsed) */}
          {rawPreview && rawPreview.length > 0 && (
            <details className="card overflow-hidden">
              <summary className="p-4 cursor-pointer hover:bg-surface-50 dark:hover:bg-surface-800">
                <span className="font-medium text-surface-900 dark:text-white">
                  Raw Response Preview
                </span>
                <span className="text-sm text-surface-500 ml-2">
                  (click to expand)
                </span>
              </summary>
              <div className="p-4 border-t border-surface-200 dark:border-surface-700">
                <pre className="text-sm text-surface-700 dark:text-surface-300 overflow-x-auto bg-surface-50 dark:bg-surface-800 p-4 rounded-lg max-h-64">
                  {JSON.stringify(rawPreview[0], null, 2)}
                </pre>
              </div>
            </details>
          )}
          
          {/* Configuration Validation Warnings */}
          {(() => {
            const warnings = [];
            
            // Check pagination without stop condition
            if (paginationType === 'page' || paginationType === 'offset') {
              if (!paginationConfig.totalPath && !paginationConfig.hasMorePath) {
                warnings.push({
                  type: 'warning',
                  title: 'No Pagination Stop Condition',
                  message: 'Configure "Total Count Path" or "Has More Path" to prevent infinite pagination loops.',
                  action: () => setStep(2)
                });
              }
            }
            
            // Check incremental sync without deduplication keys
            if (incrementalEnabled && deduplicationKeys.length === 0) {
              // Check for common ID column available but not selected for dedup
              const idCol = columns.find(c => 
                selectedColumns.has(c.name) && 
                ['id', '_id', 'uuid', 'ID', 'Id'].includes(c.name)
              );
              
              if (idCol) {
                // Show specific actionable warning with auto-fix
                warnings.push({
                  type: 'info',
                  title: 'ID Column Detected — No Deduplication Keys Set',
                  message: `Found "${idCol.name}" column. Select it as a deduplication key to prevent duplicate records during incremental syncs.`,
                  action: () => setDeduplicationKeys([idCol.name])
                });
              } else {
                // Show generic recommendation
                warnings.push({
                  type: 'recommendation',
                  title: 'No Deduplication Keys',
                  message: 'Select deduplication keys (e.g., "id") to prevent duplicate records during incremental syncs.',
                  action: null
                });
              }
            }
            
            // Check body strategy with GET method
            if (incrementalEnabled && incrementalConfig.strategy === 'body' && method === 'GET') {
              warnings.push({
                type: 'error',
                title: 'Invalid Configuration',
                message: 'Body Parameter strategy cannot be used with GET requests. Change HTTP method or sync strategy.',
                action: () => setStep(2)
              });
            }
            
            // Check incremental column is selected
            if (incrementalEnabled && incrementalConfig.column) {
              const columnExists = columns.some(c => c.name === incrementalConfig.column && selectedColumns.has(c.name));
              if (!columnExists) {
                warnings.push({
                  type: 'warning',
                  title: 'Tracking Column Not Selected',
                  message: `The incremental tracking column "${incrementalConfig.column}" is not in your selected columns.`,
                  action: null
                });
              }
            }
            
            if (warnings.length === 0) return null;
            
            return (
              <div className="space-y-2 mb-4">
                {warnings.map((warning, i) => (
                  <div 
                    key={i}
                    className={`p-3 rounded-lg border flex items-start gap-3 ${
                      warning.type === 'error' 
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
                        : warning.type === 'warning'
                        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                        : warning.type === 'recommendation'
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                        : 'bg-surface-50 dark:bg-surface-800 border-surface-200 dark:border-surface-700'
                    }`}
                  >
                    <span className={`flex-shrink-0 ${
                      warning.type === 'error' ? 'text-red-500' :
                      warning.type === 'warning' ? 'text-amber-500' :
                      warning.type === 'recommendation' ? 'text-blue-500' :
                      'text-surface-400'
                    }`}>
                      {warning.type === 'error' ? '⛔' : 
                       warning.type === 'warning' ? '⚠️' : 
                       warning.type === 'recommendation' ? '💡' : 'ℹ️'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${
                        warning.type === 'error' ? 'text-red-800 dark:text-red-200' :
                        warning.type === 'warning' ? 'text-amber-800 dark:text-amber-200' :
                        warning.type === 'recommendation' ? 'text-blue-800 dark:text-blue-200' :
                        'text-surface-700 dark:text-surface-300'
                      }`}>
                        {warning.title}
                      </p>
                      <p className={`text-xs mt-0.5 ${
                        warning.type === 'error' ? 'text-red-700 dark:text-red-300' :
                        warning.type === 'warning' ? 'text-amber-700 dark:text-amber-300' :
                        warning.type === 'recommendation' ? 'text-blue-700 dark:text-blue-300' :
                        'text-surface-600 dark:text-surface-400'
                      }`}>
                        {warning.message}
                      </p>
                    </div>
                    {warning.action && (
                      <button
                        type="button"
                        onClick={warning.action}
                        className={`flex-shrink-0 text-xs font-medium px-2 py-1 rounded ${
                          warning.type === 'error' ? 'bg-red-100 dark:bg-red-800/50 text-red-700 dark:text-red-300 hover:bg-red-200' :
                          warning.type === 'warning' ? 'bg-amber-100 dark:bg-amber-800/50 text-amber-700 dark:text-amber-300 hover:bg-amber-200' :
                          warning.type === 'recommendation' ? 'bg-blue-100 dark:bg-blue-800/50 text-blue-700 dark:text-blue-300 hover:bg-blue-200' :
                          'bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-300 hover:bg-surface-200'
                        }`}
                      >
                        Fix
                      </button>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
          
          {/* Action buttons */}
          <div className="flex items-center justify-between">
            <button onClick={() => setStep(2)} className="btn-secondary">
              Back
            </button>
            <button 
              onClick={handleCreate} 
              disabled={isCreating || !name.trim() || selectedColumns.size === 0}
              className="btn-primary"
            >
              {isCreating ? (
                <>
                  <ArrowPathIcon className="w-5 h-5 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-5 h-5" />
                  Create Data Source
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
